import React from "react"
import ReactApexChart from "react-apexcharts"

import { formatDate } from "helpers/helpers"

import KpiCard from "../parts/KpiCard"
import MetricHint from "../parts/MetricHint"
import {
	Card, EmptyNote, baseChartOptions, CHART_COLORS,
	convert, formatAmount, formatNumber, mutedStyle,
} from "../parts/common"

/**
 * Оборот: чеки, средний и медианный чек, крайние значения, каналы продаж.
 *
 * Здесь же живут «самый высокий и самый низкий чек» — это не столько метрика,
 * сколько детектор аномалий: нулевые и тестовые чеки видно сразу.
 */
function Gtv({ data, currency, rates }) {
	const months = data.months || []
	const extremes = data.extremes || {}

	if (months.length === 0) {
		return <Card><EmptyNote>Свёртка пуста за выбранный период</EmptyNote></Card>
	}

	const currentMonth = new Date().toISOString().slice(0, 7)
	const full = months.filter((row) => row.month !== currentMonth)
	const lastMonth = full.length > 0 ? full[full.length - 1] : months[months.length - 1]

	const amount = (value, month = lastMonth.month) =>
		formatAmount(convert(value, month, currency, rates), currency)
	const series = (key) => months.map((row) => Number(convert(row[key], row.month, currency, rates).toFixed(2)))
	const categories = months.map((row) => row.label)

	const chequeChart = {
		options: {
			...baseChartOptions,
			chart: { ...baseChartOptions.chart, type: "line", height: 320 },
			colors: [CHART_COLORS[0], CHART_COLORS[2], CHART_COLORS[3]],
			stroke: { width: [0, 3, 3], curve: "smooth" },
			plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
			xaxis: { ...baseChartOptions.xaxis, categories },
			yaxis: [
				{ labels: { style: { colors: "#8791a1", fontSize: "11px" }, formatter: (value) => formatNumber(value) } },
				{
					opposite: true,
					labels: { style: { colors: "#8791a1", fontSize: "11px" }, formatter: (value) => formatAmount(value, currency) },
				},
				{ show: false },
			],
		},
		series: [
			{ name: "Чеков", type: "column", data: months.map((row) => row.cheques) },
			{ name: "Средний чек", type: "line", data: series("avg_cheque") },
			{ name: "Медианный чек", type: "line", data: series("median_cheque") },
		],
	}

	const channelChart = {
		options: {
			...baseChartOptions,
			chart: { ...baseChartOptions.chart, type: "bar", height: 300, stacked: true },
			colors: [CHART_COLORS[0], CHART_COLORS[1], CHART_COLORS[2]],
			plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
			xaxis: { ...baseChartOptions.xaxis, categories },
			yaxis: {
				labels: { style: { colors: "#8791a1", fontSize: "11px" }, formatter: (value) => formatAmount(value, currency) },
			},
			tooltip: { theme: "light", y: { formatter: (value) => formatAmount(value, currency) } },
		},
		series: [
			{ name: "Зал", data: series("hall_gtv") },
			{ name: "С собой", data: series("takeaway_gtv") },
			{ name: "Доставка", data: series("delivery_gtv") },
		],
	}

	return (
		<>
			<div className="row g-2 mb-3">
				<div className="col-md-3">
					<KpiCard title="Средний чек" hintKey="metric_aov_hint"
						value={amount(lastMonth.avg_cheque)} sublabel={lastMonth.label} />
				</div>
				<div className="col-md-3">
					<KpiCard title="Медианный чек" hintKey="metric_median_cheque_hint"
						value={amount(lastMonth.median_cheque)}
						sublabel="устойчив к банкетам" accent="#9b5de5" />
				</div>
				<div className="col-md-3">
					<KpiCard title="Самый большой чек" hintKey="metric_max_cheque_hint"
						value={extremes.max ? amount(extremes.max.amount, null) : "—"}
						sublabel={extremes.max
							? `${extremes.max.pos_name} · ${formatDate(extremes.max.report_date, "dd.MM.yyyy")}`
							: null} accent="#1f9d57" />
				</div>
				<div className="col-md-3">
					<KpiCard title="Самый маленький чек" hintKey="metric_min_cheque_hint"
						value={extremes.min ? amount(extremes.min.amount, null) : "—"}
						sublabel={extremes.min
							? `${extremes.min.pos_name} · ${formatDate(extremes.min.report_date, "dd.MM.yyyy")}`
							: null} accent="#d23434" />
				</div>
			</div>

			<div className="row g-2 mb-2">
				<div className="col-md-7">
					<Card title="Чеки и средний чек" hint={<MetricHint hintKey="metric_aov_hint" />}>
						<ReactApexChart options={chequeChart.options} series={chequeChart.series} type="line" height={320} />
					</Card>
				</div>
				<div className="col-md-5">
					<Card title="Каналы продаж">
						<ReactApexChart options={channelChart.options} series={channelChart.series} type="bar" height={300} />
					</Card>
				</div>
			</div>

			<Card title="По месяцам" right={<span style={mutedStyle}>оборот показан за вычетом возвратов</span>}>
				<div className="table-responsive">
					<table className="table table-sm table-hover table-bordered mb-0" style={{ fontSize: "12px" }}>
						<thead>
							<tr>
								<th className="text-center">Месяц</th>
								<th className="text-end">Оборот</th>
								<th className="text-end">Возвраты</th>
								<th className="text-end">Чеков</th>
								<th className="text-end">Гостей</th>
								<th className="text-end">Средний чек</th>
								<th className="text-end">Медиана</th>
								<th className="text-end">Максимум</th>
								<th className="text-end">Минимум</th>
								<th className="text-end">Нулевых чеков</th>
								<th className="text-end">Оборот на точку</th>
								<th className="text-end">Чеков на точку/день</th>
							</tr>
						</thead>
						<tbody>
							{months.map((row) => (
								<tr key={row.month}>
									<td className="text-center">{row.label}</td>
									<td className="text-end">{amount(row.gtv, row.month)}</td>
									<td className="text-end">{amount(row.returns_amount, row.month)}</td>
									<td className="text-end">{formatNumber(row.cheques)}</td>
									<td className="text-end">{formatNumber(row.guests)}</td>
									<td className="text-end">{amount(row.avg_cheque, row.month)}</td>
									<td className="text-end">{amount(row.median_cheque, row.month)}</td>
									<td className="text-end">{amount(row.max_cheque, row.month)}</td>
									<td className="text-end">{amount(row.min_cheque, row.month)}</td>
									<td className="text-end">{formatNumber(row.zero_cheques)}</td>
									<td className="text-end">{amount(row.gtv_per_point, row.month)}</td>
									<td className="text-end">{Number(row.cheques_per_point || 0).toFixed(1)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</Card>
		</>
	)
}

export default Gtv
